import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { firstValueFrom } from 'rxjs';
import { ToastService } from '@workspace-app/shared/services/toast.service';
import { CertificateSharingService } from '../certificate-sharing.service';
import { ShareableOrganisationSelectComponent } from '../shareable-organisation-select/shareable-organisation-select.component';
import { CERTIFICATE_SHARING_LABELS, CertificateSharingKind, ShareableOrganisation } from '../certificate-sharing.models';

/**
 * "One organisation" mode of the bulk-download dialogs: pick an organisation, see how many current
 * certificates it has, download them as a ZIP right away (no batch, no email).
 */
@Component({
  selector: 'app-organisation-certificates-download',
  standalone: true,
  imports: [ButtonModule, DividerModule, ShareableOrganisationSelectComponent],
  template: `
    <div class="form-section">
      <label class="section-label" for="download-organisation">{{ labels().organisation }}</label>
      <app-shareable-organisation-select
        [kind]="kind()"
        inputId="download-organisation"
        testId="download-organisation-select"
        (selectionChange)="selectedOrganisation.set($event)" />
    </div>

    <p-divider />

    <div class="count-preview">
      <span class="label">Certificates to download:</span>
      <span class="value" [class.zero]="certificateCount() === 0">{{ certificateCount() }}</span>
    </div>

    <div class="info-note">
      <i class="pi pi-info-circle"></i>
      <span>The newest certificate of every current entry of the organisation is included.</span>
    </div>

    <div class="actions">
      <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="cancelled.emit()" />
      <p-button
        label="Download Zip"
        icon="pi pi-download"
        [loading]="downloading()"
        [disabled]="certificateCount() === 0 || downloading()"
        (onClick)="download()"
        data-testid="download-organisation-button" />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .form-section {
      margin-bottom: 1.5rem;

      .section-label {
        display: block;
        font-weight: 600;
        margin-bottom: 0.75rem;
        color: var(--text-color);
      }
    }

    .count-preview {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      padding: 0.75rem 1rem;
      background: var(--surface-ground);
      border-radius: var(--border-radius);
      margin-bottom: 1rem;

      .label {
        color: var(--text-color-secondary);
      }

      .value {
        font-weight: 600;
        color: var(--primary-color);
        font-size: 1.125rem;

        &.zero {
          color: var(--orange-500);
        }
      }
    }

    .info-note {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;
      padding: 0.75rem;
      background: var(--blue-50);
      border-radius: var(--border-radius);
      color: var(--blue-700);
      font-size: 0.875rem;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 1rem;
    }
  `,
})
export class OrganisationCertificatesDownloadComponent {
  private toastService = inject(ToastService);
  private certificateSharingService = inject(CertificateSharingService);

  kind = input.required<CertificateSharingKind>();
  cancelled = output<void>();
  downloaded = output<ShareableOrganisation>();

  labels = computed(() => CERTIFICATE_SHARING_LABELS[this.kind()]);

  selectedOrganisation = signal<ShareableOrganisation | null>(null);
  certificateCount = computed(() => this.selectedOrganisation()?.certificateCount ?? 0);
  downloading = signal(false);

  async download(): Promise<void> {
    const organisation = this.selectedOrganisation();
    if (!organisation || this.downloading()) return;

    this.downloading.set(true);
    try {
      const response = await firstValueFrom(
        this.certificateSharingService.downloadOrganisationCertificates(this.kind(), organisation.organisationId)
      );
      if (!response.body) {
        throw new Error('Empty response');
      }
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const filename = match ? decodeURIComponent(match[1]) : `${organisation.name}-certificates.zip`;

      const url = window.URL.createObjectURL(new Blob([response.body], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      this.toastService.displaySuccessMessage(
        `${organisation.certificateCount} certificates of ${organisation.name} downloaded`
      );
      this.downloaded.emit(organisation);
    } catch (error: unknown) {
      this.toastService.displayErrorMessage((await this.readErrorMessage(error)) || 'Failed to download the certificates');
    } finally {
      this.downloading.set(false);
    }
  }

  /** A blob request returns its JSON error body as a Blob — read the message out of it. */
  private async readErrorMessage(error: unknown): Promise<string | undefined> {
    const body = (error as { error?: unknown })?.error;
    if (body instanceof Blob) {
      try {
        return (JSON.parse(await body.text()) as { message?: string }).message;
      } catch {
        return undefined;
      }
    }
    return (body as { message?: string } | undefined)?.message;
  }
}
